import AppHeader from "@/components/AppHeader";
import { useUserRole } from "@/hooks/useUserRole";
import { HowToTab } from "@/pages/super-admin-console/HowToTab";
import { SuperAdminsSection } from "@/pages/super-admin-console/SuperAdminsSection";
import { TenantsSection } from "@/pages/super-admin-console/TenantsSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@simplicity/ui";
import { ArrowLeft, BookOpen, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

const SuperAdminConsole = () => {
  const { isSuperAdmin, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  // Lifted from TenantsSection so the full-page loader covers the initial
  // tenant fetch exactly as the pre-refactor page did.
  const [tenantsLoading, setTenantsLoading] = useState(true);

  useEffect(() => {
    if (!roleLoading && !isSuperAdmin) {
      void navigate("/");
    }
  }, [isSuperAdmin, roleLoading, navigate]);

  const showLoader = roleLoading || tenantsLoading;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {showLoader && (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      )}
      {/* Sections mount only for super admins (matches the pre-refactor guard) and
          drive `tenantsLoading`; kept mounted-but-hidden behind the loader until the
          initial fetch resolves so the page swaps from loader to content in one paint. */}
      {isSuperAdmin && (
        <div className={showLoader ? "hidden" : "contents"}>
          <AppHeader />
          <main className="flex-1 p-10 max-w-3xl mx-auto w-full space-y-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboards
            </Link>
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-6 w-6 text-destructive" />
              <h1 className="text-2xl font-semibold">Super Admin Console</h1>
            </div>

            <Tabs defaultValue="manage" className="w-full">
              <TabsList className="mb-8">
                <TabsTrigger value="manage">Manage</TabsTrigger>
                <TabsTrigger value="howto" className="gap-1.5">
                  <BookOpen className="h-4 w-4" /> How To
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manage" className="space-y-12">
                {error && (
                  <div className="p-3 border border-destructive rounded text-sm text-destructive bg-destructive/10">
                    {error}
                  </div>
                )}

                <TenantsSection onError={setError} onLoadingChange={setTenantsLoading} />
                <SuperAdminsSection onError={setError} />
              </TabsContent>

              <TabsContent value="howto" className="space-y-8">
                <HowToTab />
              </TabsContent>
            </Tabs>
          </main>
        </div>
      )}
    </div>
  );
};

export default SuperAdminConsole;
